import paths from 'routes/paths';
import type { GuardTarget, NavigationGuardRegistry } from './navigationGuards';

export const allowNavigation = (location: GuardTarget, action: string) => {
  // We don't want to block navigation during logout
  const loginPath = paths.get('login');
  return (
    action === 'REPLACE' &&
    typeof loginPath === 'string' &&
    location.pathname === loginPath
  );
};

export type WhenCondition = (args: {
  location: GuardTarget;
  action: string;
}) => unknown;

export interface ConfirmNavigationState {
  when: unknown;
  processing: unknown;
  whenCondition?: WhenCondition | null;
}

export const confirmNavigationBlocks = (
  { when, processing, whenCondition }: ConfirmNavigationState,
  location: GuardTarget,
  action: string
): boolean => {
  if (!when || processing) {
    return false;
  }
  if (allowNavigation(location, action)) {
    return false;
  }
  if (
    typeof whenCondition === 'function' &&
    !whenCondition({ location, action })
  ) {
    return false;
  }
  return true;
};

export interface GuardedPromptMessageOptions {
  registry: NavigationGuardRegistry;
  blocks: (location: GuardTarget, action: string) => boolean;
  onBypass?: (location: GuardTarget, action: string) => void;
  onBlocked: (location: GuardTarget, action: string) => void;
}

export const createGuardedPromptMessage =
  ({ registry, blocks, onBypass, onBlocked }: GuardedPromptMessageOptions) =>
  (location: GuardTarget, action: string): boolean => {
    if (!blocks(location, action)) {
      return true;
    }
    if (registry.consumeBypass()) {
      onBypass?.(location, action);
      return true;
    }
    onBlocked(location, action);
    return false;
  };
